import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { ExternalLink } from "lucide-mui";
import { Link as RouterLink } from "react-router-dom";
import type { CompanyContact } from "../../../api/client.ts";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import { useTenant } from "../../../contexts/TenantContext.tsx";
import FormattedDate from "../../../components/FormattedDate.tsx";
import Panel from "../../../components/Panel.tsx";
import { humanize } from "../../../utils/format.ts";

interface ContactsCardProps {
  contacts: CompanyContact[];
}

export default function ContactsCard({ contacts }: ContactsCardProps) {
  const { tenantPath } = useTenant();
  if (contacts.length === 0) return null;
  return (
    <Panel title="Contacts" count={contacts.length} sx={{ mb: 3 }}>
      <ScrollTable>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Phone</TableCell>
            <TableCell>From</TableCell>
            <TableCell>To</TableCell>
            <TableCell>Source</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {contacts.map((ct, idx) => (
            <TableRow key={`${ct.contact_id}-${ct.role_type}-${idx}`}>
              <TableCell>
                <Link
                  component={RouterLink}
                  to={tenantPath(`/contacts/${ct.contact_id}`)}
                >
                  {ct.full_name}
                </Link>
              </TableCell>
              <TableCell>
                {ct.role_title_en || humanize(ct.role_type.toLowerCase())}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  variant="outlined"
                  label={ct.is_active ? "Active" : "Departed"}
                  color={ct.is_active ? "success" : "default"}
                />
              </TableCell>
              <TableCell>
                {ct.email ? <Link href={`mailto:${ct.email}`}>{ct.email}</Link> : "-"}
              </TableCell>
              <TableCell>
                {ct.phone ? <Link href={`tel:${ct.phone}`}>{ct.phone}</Link> : "-"}
              </TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <FormattedDate value={ct.from_date} format="month" />
              </TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <FormattedDate value={ct.to_date} format="month" />
              </TableCell>
              <TableCell>
                {ct.linkedin_url ? (
                  <Link
                    href={`https://${ct.linkedin_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {ct.source} <ExternalLink fontSize="inherit" />
                  </Link>
                ) : (
                  ct.source
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </ScrollTable>
    </Panel>
  );
}
